import { useTheme } from '@/hooks/useTheme';

interface HeroSectionProps {
  onBookAudit: () => void;
}

export default function HeroSection({ onBookAudit }: HeroSectionProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <section
      id="hero"
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden transition-colors duration-500"
      style={{
        background: isDark
          ? 'linear-gradient(160deg, #0A0D12 0%, #0E1116 40%, #111827 100%)'
          : 'linear-gradient(160deg, #F5F5F7 0%, #ffffff 40%, #F0F0F2 100%)'
      }}
    >
      {/* Full-bleed background image */}
      <div className="absolute inset-0 pointer-events-none">
        <img
          src="https://public.readdy.ai/ai/img_res/d4e2d735666a035fb92ae112514ca17d.jpg"
          alt=""
          className="w-full h-full object-cover object-center"
          style={{ opacity: isDark ? 0.55 : 0.50 }}
        />
        {/* Overlay for readability */}
        <div
          className="absolute inset-0"
          style={{
            background: isDark
              ? 'linear-gradient(160deg, rgba(10,13,18,0.75) 0%, rgba(14,17,22,0.55) 40%, rgba(17,24,39,0.70) 100%)'
              : 'linear-gradient(160deg, rgba(245,245,247,0.52) 0%, rgba(255,255,255,0.42) 40%, rgba(240,240,242,0.52) 100%)'
          }}
        />
      </div>

      {/* Radial glow backdrop */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] pointer-events-none"
        style={{
          background: isDark
            ? 'radial-gradient(ellipse at 50% 0%, rgba(34,211,238,0.08) 0%, transparent 65%)'
            : 'radial-gradient(ellipse at 50% 0%, rgba(0,113,227,0.06) 0%, transparent 65%)'
        }}
      />
      <div
        className="absolute top-1/3 right-0 w-[600px] h-[600px] pointer-events-none"
        style={{
          background: isDark
            ? 'radial-gradient(ellipse at 100% 50%, rgba(139,92,246,0.07) 0%, transparent 65%)'
            : 'radial-gradient(ellipse at 100% 50%, rgba(88,86,214,0.05) 0%, transparent 65%)'
        }}
      />

      {/* Subtle grid */}
      <div
        className="absolute inset-0 pointer-events-none opacity-40"
        style={{
          backgroundImage: isDark
            ? `linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)`
            : `linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Content */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-6 lg:px-10 pt-32 pb-20 flex flex-col items-center text-center">

        {/* Badge */}
        <div className={`animate-fade-up inline-flex items-center gap-2.5 px-5 py-2 rounded-full border backdrop-blur-sm mb-10 ${
          isDark
            ? 'border-white/10 bg-white/4'
            : 'border-black/8 bg-white/60'
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full inline-block animate-pulse ${isDark ? 'bg-cyan-400' : 'bg-blue-500'}`}></span>
          <span className={`text-xs tracking-[0.15em] uppercase font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Operational Intelligence</span>
        </div>

        {/* Headline */}
        <h1 className={`animate-fade-up-delay-1 text-5xl md:text-7xl lg:text-[90px] font-bold leading-[1.04] tracking-[-0.03em] mb-6 max-w-5xl ${
          isDark ? 'text-white' : 'text-[#1d1d1f]'
        }`}>
          Intelligent Systems
          <br />
          <span className="text-gradient-cyan">Rooted in Structure</span>
          <br />
          and Meaning
        </h1>

        {/* Sub */}
        <p className={`animate-fade-up-delay-2 text-lg md:text-xl leading-relaxed max-w-2xl mb-12 font-light tracking-wide ${
          isDark ? 'text-gray-400' : 'text-[#6e6e73]'
        }`}>
          PuleAI blends disciplined automation with intentional design—where every system operates with precision, purpose, and continuity.
        </p>

        {/* CTAs */}
        <div className="animate-fade-up-delay-3 flex flex-col sm:flex-row items-center gap-4 mb-20">
          <button
            onClick={onBookAudit}
            className={`cursor-pointer whitespace-nowrap px-8 py-4 rounded-full text-sm font-semibold transition-all duration-200 tracking-wide ${
              isDark
                ? 'bg-white text-[#0E1116] hover:bg-gray-100'
                : 'bg-[#0E1116] text-white hover:bg-gray-800'
            }`}
          >
            Book a System Audit
          </button>
          <a
            href="#services"
            className={`cursor-pointer whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full border text-sm font-medium transition-all duration-200 tracking-wide ${
              isDark
                ? 'border-white/15 text-gray-300 hover:text-white hover:border-white/30'
                : 'border-black/15 text-gray-600 hover:text-gray-900 hover:border-black/30'
            }`}
          >
            Explore Solutions
            <i className="ri-arrow-right-line text-sm"></i>
          </a>
        </div>

        {/* Dashboard Visual */}
        <div className="w-full max-w-5xl mx-auto relative animate-fade-up-delay-3">
          {/* Glow beneath image */}
          <div
            className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-3/4 h-32 pointer-events-none"
            style={{
              background: isDark
                ? 'radial-gradient(ellipse at 50% 100%, rgba(34,211,238,0.15) 0%, transparent 70%)'
                : 'radial-gradient(ellipse at 50% 100%, rgba(0,113,227,0.1) 0%, transparent 70%)'
            }}
          />

          {/* Frame */}
          <div className={`relative rounded-2xl overflow-hidden border backdrop-blur-sm transition-colors duration-500 ${
            isDark
              ? 'border-white/10 bg-[#111827]/60'
              : 'border-black/10 bg-white/80 shadow-xl'
          }`}>
            {/* Window chrome */}
            <div className={`flex items-center gap-2 px-5 py-3.5 border-b transition-colors duration-500 ${
              isDark
                ? 'border-white/8 bg-white/3'
                : 'border-black/5 bg-gray-50/50'
            }`}>
              <div className="w-3 h-3 rounded-full bg-red-500/60"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500/60"></div>
              <div className="w-3 h-3 rounded-full bg-green-500/60"></div>
              <div className={`ml-4 flex-1 h-5 rounded-full max-w-xs ${isDark ? 'bg-white/5' : 'bg-black/5'}`}></div>
              <div className="flex items-center gap-1.5 ml-auto">
                <div className={`w-1.5 h-1.5 rounded-full animate-pulse ${isDark ? 'bg-cyan-400' : 'bg-blue-500'}`}></div>
                <span className={`text-xs font-medium tracking-wide ${isDark ? 'text-cyan-400/80' : 'text-blue-500/80'}`}>LIVE</span>
              </div>
            </div>

            {/* Dashboard Image */}
            <div className="w-full" style={{ height: '460px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=futuristic%20dark%20AI%20operations%20dashboard%20interface%20showing%20real%20time%20data%20analytics%20with%20glowing%20cyan%20and%20violet%20accent%20lines%20on%20deep%20navy%20dark%20background%20clean%20minimal%20enterprise%20software%20UI%20with%20charts%20metrics%20workflow%20nodes%20and%20status%20indicators%20professional%20high%20tech%20aesthetic&width=1200&height=460&seq=hero-dashboard-1&orientation=landscape"
                alt="PuleAI Operations Dashboard"
                className="w-full h-full object-cover object-top"
              />
            </div>

            {/* Overlay gradient at bottom of image */}
            <div
              className="absolute bottom-0 left-0 right-0 h-24 pointer-events-none"
              style={{
                background: isDark
                  ? 'linear-gradient(to top, #0E1116 0%, transparent 100%)'
                  : 'linear-gradient(to top, #F5F5F7 0%, transparent 100%)'
              }}
            />

            {/* Floating stat badges */}
            <div className={`absolute top-16 left-6 px-4 py-3 rounded-xl border backdrop-blur-md transition-colors duration-500 ${
              isDark
                ? 'border-cyan-400/20 bg-[#0E1116]/80'
                : 'border-blue-500/20 bg-white/90 shadow-lg'
            }`}>
              <p className={`text-xs mb-0.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Response Rate</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-cyan-400' : 'text-blue-500'}`}>98.7%</p>
            </div>
            <div className={`absolute top-16 right-6 px-4 py-3 rounded-xl border backdrop-blur-md transition-colors duration-500 ${
              isDark
                ? 'border-violet-400/20 bg-[#0E1116]/80'
                : 'border-purple-500/20 bg-white/90 shadow-lg'
            }`}>
              <p className={`text-xs mb-0.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>Time Saved</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-violet-400' : 'text-purple-500'}`}>4.2x</p>
            </div>
            <div className={`absolute bottom-16 left-1/2 -translate-x-1/2 px-5 py-3 rounded-xl border backdrop-blur-md flex items-center gap-3 transition-colors duration-500 ${
              isDark
                ? 'border-white/10 bg-[#0E1116]/80'
                : 'border-black/10 bg-white/90 shadow-lg'
            }`}>
              <div className={`w-2 h-2 rounded-full animate-pulse ${isDark ? 'bg-green-400' : 'bg-green-500'}`}></div>
              <p className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>All systems operational</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}